import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { api, User, BadgeCounts } from './lib/api';
import { RouteErrorBoundary } from './components/ErrorBoundary';

export interface AppOutletContext {
  currentUser: User | null;
  badges: BadgeCounts;
  refreshBadges: () => void;
}

const ZERO_BADGES: BadgeCounts = { triage_pending: 0, gate_pending: 0, attention: 0, degrading_unacked: 0 };

// SVG Icons
const DashboardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const ClientsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const ValidateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const TranscriptIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const RoadmapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
    <polyline points="2 17 12 22 22 17"/>
    <polyline points="2 12 12 17 22 12"/>
  </svg>
);

const PulseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);




const HamburgerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const SIDEBAR_PINNED_KEY = 'mnemo_sidebar_pinned';

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/';
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [badges, setBadges] = useState<BadgeCounts>(ZERO_BADGES);

  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_PINNED_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    // fetchAPI already redirects to /login on a 401 (expired/invalid
    // session), so there's nothing else to do here on failure.
    api.getCurrentUser()
      .then(setCurrentUser)
      .catch(() => {});
  }, []);

  const refreshBadges = () => {
    api.getBadges().then(setBadges).catch(() => {});
  };

  useEffect(() => {
    refreshBadges();
  }, [location.pathname]);

  const isFounder = currentUser?.role === 'founder';
  const isAdmin = currentUser?.role === 'admin';
  const isInvestor = currentUser?.role === 'investor';
  const isUploader = currentUser?.role === 'uploader';
  const hasElevatedAccess = isFounder || isAdmin;

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      // Ignore - clear locally and redirect regardless of server response.
    } finally {
      navigate('/login');
    }
  };

  const toggleSidebarPin = () => {
    const newValue = !sidebarPinned;
    setSidebarPinned(newValue);
    localStorage.setItem(SIDEBAR_PINNED_KEY, String(newValue));
  };

  const shouldCollapse = isDashboard && !sidebarPinned;

  const pulseCount = badges.attention + badges.degrading_unacked;

  return (
    <div className="app">
      <aside className={`sidebar ${shouldCollapse ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <img src="/logo.png" alt="Cap8" className="sidebar-logo" />
          <div>
            <div className="sidebar-title">Mnemo</div>
            <div className="sidebar-subtitle">Insight Engine</div>
          </div>
          {isDashboard && (
            <button
              className="sidebar-pin-btn"
              onClick={toggleSidebarPin}
              title={sidebarPinned ? 'Collapse sidebar' : 'Pin sidebar open'}
            >
              <HamburgerIcon />
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {/* ── MNEMO ── */}
          <div className="sidebar-section">Mnemo</div>

          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `sidebar-link sidebar-link-primary ${isActive ? 'active' : ''}`
            }
          >
            <DashboardIcon />
            <span>Ask</span>
          </NavLink>

          {!isUploader && (
            <>
              <NavLink
                to="/roadmap"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <RoadmapIcon />
                <span>Roadmap</span>
                {!!badges.triage_pending && (
                  <span className="badge badge-red nav-badge">{badges.triage_pending}</span>
                )}
              </NavLink>

              <NavLink
                to="/clients"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <ClientsIcon />
                <span>Clients</span>
              </NavLink>

              <NavLink
                to="/pulse"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <PulseIcon />
                <span>Pulse</span>
                {!!pulseCount && (
                  <span className="badge badge-amber nav-badge">{pulseCount}</span>
                )}
              </NavLink>
            </>
          )}

          {/* ── OPERATIONS ── */}
          {!isInvestor && (
            <>
              <div className="sidebar-section">Operations</div>

              <NavLink
                to="/capture"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <TranscriptIcon />
                <span>Capture</span>
              </NavLink>

              <NavLink
                to="/validate"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <ValidateIcon />
                <span>Validate</span>
                {!!badges.gate_pending && (
                  <span className="badge badge-red nav-badge">{badges.gate_pending}</span>
                )}
              </NavLink>
            </>
          )}

          {/* ── SETTINGS ── */}
          {hasElevatedAccess && (
            <>
              <div className="sidebar-section">Settings</div>

              <NavLink
                to="/settings/market-mood"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <SettingsIcon />
                <span>Market Mood</span>
              </NavLink>

              {isAdmin && (
                <NavLink
                  to="/settings/users"
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                >
                  <UsersIcon />
                  <span>Users</span>
                </NavLink>
              )}
            </>
          )}

        </nav>

        <div className="sidebar-footer">
          <button className="btn btn-ghost btn-small" onClick={handleLogout} style={{ width: '100%', justifyContent: 'flex-start' }}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className="main-content">
        <RouteErrorBoundary>
          <Outlet context={{ currentUser, badges, refreshBadges } as AppOutletContext} />
        </RouteErrorBoundary>
      </main>
    </div>
  );
}
